import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    ImageResourceItem,
    Inject,
    ModelEvents,
    SoundType,
} from '@denishussar/html5-game-engine';
import {Point, Texture} from 'pixi.js';
import {AnimationContents} from '../../../AnimationContents';
import {ButtonController, IButtonControllerOptions} from '../../Common/ButtonController';
import {easeInOutCubic, Easing} from '../../Common/Easing';
import {ModalsNames} from '../../Common/MadalsManager/ModalsList';
import {SoundConstants} from '../../Constants/Sounds';
import {BlueButtonTextStyle, GreenButtonTextStyle, YellowTextBoldStyle} from '../../Constants/TextStyles';
import {LocalizationContents} from '../../LocalizationContents';
import {GameLogic} from '../../Logic/GameLogic';
import {GameModel, GameStateEnum} from '../../Models/GameModel';
import {SoundModel} from '../../Models/SoundModel';
import {GamePictureController, IGamePictureControllerOption} from '../Game/GamePictureController';
import {ConfirmationsYouRewardType} from '../Modals/Confirmations/ConfirmationsYouRewardController';
import {GameEvent, GameEventSceneButtonClick} from '../../Constants/GameEvent';
import {IConfirmationsConfirmControllerOptions} from "../Modals/Confirmations/ConfirmationsConfirmController";
import {AdsLogic} from "../../Logic/Ads/AdsLogic";
import {REWARDED_ADS_TYPE} from "../../Logic/Ads/BaseAdsLogic";

export interface IWinScreenControllerOptions {
    opacity: boolean
}

export class WinScreenController extends AnimationBaseController {
    $model: GameModel;

    //todo: something strange (rework?)
    _width: number;
    _height: number;
    _scale: number;

    protected picturecContainer: PIXI.Container;
    protected frameSize: number = 248;
    protected frameSizeInitialized = 248;

    protected frame: PIXI.mesh.NineSlicePlane;
    protected picture: GamePictureController;

    protected pictureTexture: Texture;

    protected easings: Array<Easing> = [];
    protected continueButton: ButtonController;
    protected x2Button: ButtonController;
    protected timerId;

    public constructor(
        @Inject protected options: IWinScreenControllerOptions,
        @Inject protected gameLogic: GameLogic,
        @Inject protected adsLogic: AdsLogic,
        @Inject protected modelEvents: ModelEvents,
        @Inject protected model: GameModel,
    ) {
        super('WinScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        const resources = {
            'frame': new ImageResourceItem('frame', 'picture_frame.png'),
        };

        return <IAnimationControllerConfiguration>{
            resources,
            animationContent: AnimationContents.WIN_SCREEN_JSON,
            libraryName: 'win_screen',
            sounds: [
                {
                    id: SoundConstants.WIN_SCREEN,
                    type: SoundType.Music,
                },
            ],
            modelEvents: {
                onTextureCreated: e => {
                    this.handleTextureCreated();
                },
            },
            childControllers: {
                picture: {
                    controller: GamePictureController,
                    model: this.$model,
                    options: <IGamePictureControllerOption>{
                        fillColorsAfterInitializtion: true
                    }
                },
                btn_share: {
                    instanceName: 'container.btn_share',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        label: LocalizationContents.getText('x2 Reward'),
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.sceneButtonClick, <GameEventSceneButtonClick>{ buttonName: 'x2 Reward', status });

                            if (!status) {
                                return;
                            }

                            if (this.adsLogic.isReady && this.adsLogic.adsLogic.hasAdBlock()) {
                                this.gameLogic.modalsManager.hide();
                                this.gameLogic.modalsManager.show(ModalsNames.confirm, <IConfirmationsConfirmControllerOptions>{
                                    oneButtonMode: true,
                                    text: LocalizationContents.getText('Adblock'),
                                    yesText: LocalizationContents.getText('Ok'),
                                    yesClick: () => {
                                        this.gameLogic.modalsManager.hide()
                                    },
                                });
                            } else {
                                this.x2Button.disabled();
                                this.showAdvertisment()
                                    .catch(() => {
                                        console.log('something wrong with ads');
                                    })
                                    .finally(() => {
                                        const reward = this.gameLogic.balanceManager.getPictureReward(this.pictureTexture.width) * 2;

                                        const onClose = () => {
                                            this.gameLogic.modalsManager.hide();
                                            this.gameLogic.addBalanceCoins(reward);
                                            this.gameLogic.setState(GameStateEnum.main);

                                            /*
                                            this.analytics.sendEvent(AnalyticsEventEnum.exitEndscreen, {
                                                picture_id: this.$model.picture.id,
                                                user_id: this.$model.userModel.id,
                                                method_id: 'ads',
                                            });
                                             */
                                        };

                                        this.gameLogic.modalsManager.show(ModalsNames.youReward, {
                                            type: ConfirmationsYouRewardType.money,
                                            value: reward,
                                            text: LocalizationContents.getText('Your Reward'),
                                            okText: LocalizationContents.getText('Thanks'),
                                            okClick: () => onClose(),
                                            exitClick: () => onClose(),
                                        });
                                    })
                            }
                        },
                    },
                    style: GreenButtonTextStyle,
                },
                btn_continue: {
                    instanceName: 'container.btn_continue',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        label: LocalizationContents.getText('Continue'),
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.sceneButtonClick, <GameEventSceneButtonClick>{ buttonName: 'Next', status });

                            if (!status) {
                                return;
                            }

                            this.handleButtonNextClick();
                        },
                    },
                },
            },
        };
    }

    private timerUpdate(): void {
        // if (!this.adsLogic.isReady) return;
        const time = Date.now() - this.$model.addsCooldownTimestamp;
        if (this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.WIN_MULTIPLIER_X2)) {
            // this.hideTimer();
            this.x2Button.label = LocalizationContents.getText('x2 Reward');
            this.x2Button.enabled();
            return;
        }

        const date = new Date(this.adsLogic.adsLogic.timeoutBeforeRewardedAd(REWARDED_ADS_TYPE.WIN_MULTIPLIER_X2));
        const hours = Math.min(3, date.getUTCHours());
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        this.x2Button.label = `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;

        this.timerId = setTimeout(() => {
            this.timerUpdate();
        }, 1000);
    }

    protected onDestroy(): void {
        clearTimeout(this.timerId);
    }

    protected onInitialize() {
        this._width = this.width;
        this._height = this.height;

        this.picturecContainer = new PIXI.Container();
        this.display.addChild(this.picturecContainer);

        this.pictureTexture = this.gameLogic.getPictureTexture();

        this.continueButton = this.$childControllers.get<ButtonController>('btn_continue');
        this.x2Button = this.$childControllers.get<ButtonController>('btn_share');

        if (this.adsLogic.isReady) {
            if (!this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.WIN_MULTIPLIER_X2)) {
                this.timerUpdate();
                this.x2Button.disabled();
            }
        }

        this.x2Button.visible = false;
        this.continueButton.x = 0;

        /*
        this.analytics.sendEvent(AnalyticsEventEnum.enterEndscreen, {
            picture_id: this.$model.picture.id,
            user_id: this.$model.userModel.id,
            date_time: Date.now(),
        });
         */

        this.soundManager.play(SoundConstants.WIN_SCREEN, false, SoundModel.fx);

        const reward = this.gameLogic.balanceManager.getPictureReward(this.pictureTexture.width);

        this.setTextStyleByQuery('container.btn_continue.label', BlueButtonTextStyle);
        this.setTextStyleByQuery('container.btn_share.label', GreenButtonTextStyle);

        this.setTextByQuery('container.coins_win.txt', `x${reward.toString()}`);

        this.setTextStyleByQuery('container.coins_win.txt', YellowTextBoldStyle);
        this.setTextStyleByQuery('container.needle_win.txt', YellowTextBoldStyle);
        this.getDisplayObjectByQuery('container.needle_win')[0].visible = false;

        if (this.options.opacity) {
            this.display.alpha = 0;
        }
    }

    protected onUpdate(frameRate: number) {
        this.easings.forEach((easing) => {
            easing.update(frameRate);
        });
    }

    protected onResize(width: number, height: number, scale: number) {
        this._width = width;
        this._height = height;
        this._scale = scale;

        if (!this.frame) {
            if (this.$model.saveTexture) {
                this.handleTextureCreated();
            }
            return;
        }
    }

    protected handleButtonNextClick(): void {
        const reward = this.gameLogic.balanceManager.getPictureReward(this.pictureTexture.width);
        const onClose = () => {
            this.gameLogic.modalsManager.hide();
            this.gameLogic.addBalanceCoins(reward);
            this.gameLogic.setState(GameStateEnum.main);

            /*
            this.analytics.sendEvent(AnalyticsEventEnum.exitEndscreen, {
                picture_id: this.$model.picture.id,
                user_id: this.$model.userModel.id,
                method_id: 'coins',
            });
             */
        };

        this.gameLogic.modalsManager.show(ModalsNames.youReward, {
            type: ConfirmationsYouRewardType.money,
            value: reward,
            text: LocalizationContents.getText('Your Reward'),
            okText: LocalizationContents.getText('Thanks'),
            okClick: () => onClose(),
            exitClick: () => onClose(),
        });
    }

    protected handleTextureCreated(): void {
        const margin = 64;
        const pictureSize = {
            width: this.gameLogic.getPictureSize().width * this.gameLogic.getPixelSize(),
            height: this.gameLogic.getPictureSize().height * this.gameLogic.getPixelSize(),
        };

        if (!this.frame) {
            const frameTexture = this.getResources().filter((res) => res.name === 'frame')[0].getTexture();
            this.frame = new PIXI.mesh.NineSlicePlane(frameTexture, this.frameSize, this.frameSize, this.frameSize, this.frameSize);
            this.frame.interactive = false;
            this.picturecContainer.addChild(this.frame);
        }
        this.frame.width = pictureSize.width + 2 * margin;
        this.frame.height = pictureSize.height + 2 * margin;
        this.frame.position.set(-this.frame.width / 2, -this.frame.height / 2);
        this.frameSizeInitialized = this.frame.width;

        if (!this.picture) {
            this.picture = this.$childControllers.get<GamePictureController>('picture');
            this.picturecContainer.addChild(this.picture.display);
        }
        this.picture.display.position.set(this.frame.position.x + margin, this.frame.position.y + margin);

        this.center();
        this.animatePictureZooming(this.getMinScale(), new PIXI.Point(0, -100));
    }

    private async showAdvertisment(): Promise<void> {
        this.soundManager.muteSoundsByType(true, SoundType.ALL);
        if (!this.adsLogic.isReady) return Promise.resolve();

        this.adsLogic.adsLogic.skipNextInterstitial = true;
        return this.adsLogic.adsLogic.requestAd()
            .finally(() => {
                this.modelEvents.dispatch(this.model, GameEvent.makePurchase, {
                    sceneName: "Game",
                    popupName: "EndScreen",
                    name: "x2Reward",
                    type: "ads"
                })

                this.adsLogic.adsLogic.restartRewardedAdTimeout(REWARDED_ADS_TYPE.WIN_MULTIPLIER_X2);

                this.soundManager.muteSoundsByType(false, SoundType.ALL);
            });
    }

    protected center(): void {
        if (!this.frame) {
            return;
        }

        this.picturecContainer.position.set(0, 0);
    }

    protected animatePictureZooming(endScale: number, endPosition: Point): void {
        this.easings.splice(0);

        const timeout = 600;
        const startScale = this.picturecContainer.scale.y;

        const startPosition = this.picturecContainer.position.clone();

        const easing = new Easing(
            [startScale, startPosition.x, startPosition.y],
            [endScale, endPosition.x, endPosition.y],
            timeout,
            easeInOutCubic
        );

        easing.onComplete = () => {
            for (let i = 0; i < this.easings.length; i++) {
                if (this.easings[i] === easing) {
                    this.easings.splice(i, 1);
                    break;
                }
            }
        };
        easing.onStep = (values: Array<number>) => {
            if (this.picturecContainer) {
                this.picturecContainer.scale.set(values[0]);
                this.picturecContainer.position.set(values[1], values[2]);
            }
        };
        this.easings.push(easing);
    }

    protected getMinScale(): number {
        const minSize = Math.min(this._width, this._height * .6);
        const scale = minSize / this.frameSizeInitialized;

        return scale / this._scale;
    }
}
